import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import { Plus, Trash2 } from "react-feather";
import { useTranslation } from "react-i18next";
import { titleCase } from "../../utils/case";
import {
  LANGS,
  emptyLang,
} from "../../utils/pageHelpers";
import {
  ICON_MAP,
} from "../../utils/icons";
import { hasRole } from "../../utils/auth";
import { getImageUrl } from "../../utils/image";

const ArrayLangInput = ({
  t,
  content,
  arrayField,
  index,
  field,
  label,
  type = "text",
  updateArrayLangField,
  disabled,
}) => (
  <Row>
    {LANGS.map((lang) => (
      <Col md="4" key={lang}>
        <FormGroup>
          <Label>
            {titleCase(t(label))} - {lang.toUpperCase()}
          </Label>

          <Input
            type={type}
            rows={type === "textarea" ? 3 : undefined}
            value={
              content[arrayField]?.[index]?.[field]?.[lang] ||
              ""
            }
            onChange={(e) =>
              updateArrayLangField(
                arrayField,
                index,
                field,
                lang,
                e.target.value
              )
            }
            disabled={disabled}
          />
        </FormGroup>
      </Col>
    ))}
  </Row>
);

const Events = ({
  content,
  lots = [],
  updateArrayField,
  updateArrayLangField,
  addArrayItem,
  removeArrayItem,
  handleArrayImageUpload,
}) => {
  const { t } = useTranslation();

  const canManage = hasRole(
    "superadmin",
    "finance"
  );

  const events = content.events || [];

  const eventLots = lots.filter(
    (lot) =>
      lot.isEvent === true
  );

  const addEvent = () => {
    addArrayItem("events", {
      heroImage: "",
      lotId: null,
      tag: emptyLang(),
      startDate: "",
      endDate: "",
      additionalFeatures: [],
    });
  };

  const removeEvent = (index) => {
    removeArrayItem("events", index);
  };

  const addFeature = (eventIndex) => {
    const event = events[eventIndex];

    updateArrayField(
      "events",
      eventIndex,
      "additionalFeatures",
      [
        ...(event?.additionalFeatures || []),
        {
          icon: "",
          description: emptyLang(),
        },
      ]
    );
  };

  const removeFeature = (
    eventIndex,
    featureIndex
  ) => {
    const event = events[eventIndex];

    const features = (
      event?.additionalFeatures || []
    ).filter(
      (_, index) => index !== featureIndex
    );

    updateArrayField(
      "events",
      eventIndex,
      "additionalFeatures",
      features
    );
  };

  const updateFeatureField = (
    eventIndex,
    featureIndex,
    field,
    value
  ) => {
    const event = events[eventIndex];

    const features = [
      ...(event?.additionalFeatures || []),
    ];

    features[featureIndex] = {
      ...features[featureIndex],
      [field]: value,
    };

    updateArrayField(
      "events",
      eventIndex,
      "additionalFeatures",
      features
    );
  };

  const updateFeatureLangField = (
    eventIndex,
    featureIndex,
    lang,
    value
  ) => {
    const event = events[eventIndex];

    const features = [
      ...(event?.additionalFeatures || []),
    ];

    features[featureIndex] = {
      ...features[featureIndex],
      description: {
        ...(features[featureIndex]?.description ||
          emptyLang()),
        [lang]: value,
      },
    };

    updateArrayField(
      "events",
      eventIndex,
      "additionalFeatures",
      features
    );
  };

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-1">
                {titleCase(t("events"))}
              </h6>

              <small className="text-muted">
                {titleCase(
                  t(
                    "manage events displayed on the booking portal"
                  )
                )}
              </small>
            </div>

            {canManage && (
              <Button
                type="button"
                size="sm"
                color="primary"
                className="d-flex align-items-center gap-1"
                onClick={addEvent}
              >
                <Plus size={16} />
                {titleCase(t("add event"))}
              </Button>
            )}
          </div>

          {events.length === 0 && (
            <div className="text-center text-muted py-4 border rounded">
              {titleCase(
                t("no events configured")
              )}
            </div>
          )}

          {events.map((event, eventIndex) => (
            <Card
              className="mb-3 border"
              key={eventIndex}
            >
              <CardBody>
                {/* Event header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0">{titleCase(t("event"))} {eventIndex + 1}</h6>
                  {canManage && (
                    <Button
                      type="button"
                      size="sm"
                      color="danger"
                      outline
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "32px", padding: 0 }}
                      onClick={() => removeEvent(eventIndex)}
                      title={titleCase(t("remove event"))}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>

                <FormGroup className="mb-4">
                  <Label>{titleCase(t("hero image"))}</Label>
                  {event.heroImage && (
                    <div className="mb-3">
                      <img
                        src={getImageUrl(event.heroImage)}
                        alt={titleCase(t("event hero"))}
                        className="img-fluid rounded border"
                        style={{ width: "100%", maxHeight: "300px", objectFit: "cover" }}
                      />
                    </div>
                  )}
                  {canManage && (
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        handleArrayImageUpload("events", eventIndex, "heroImage", file);
                        e.target.value = "";
                      }}
                    />
                  )}
                  <small className="text-muted d-block mt-2">
                    {titleCase(t("this image will be used as the hero image for this event"))}
                  </small>
                </FormGroup>

                {/* Lot */}
                <FormGroup className="mb-4">
                  <Label>{titleCase(t("event lot"))}</Label>
                  <Input
                    type="select"
                    value={event.lotId || ""}
                    onChange={(e) =>
                      updateArrayField("events", eventIndex, "lotId", e.target.value ? Number(e.target.value) : null)
                    }
                    disabled={!canManage}
                  >
                    <option value="">{titleCase(t("select event lot"))}</option>
                    {eventLots.map((lot) => (
                      <option key={lot.id} value={lot.id}>
                        {lot.location ? `${lot.location} - ` : ""}{lot.name}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    {titleCase(t("only lots configured as events are available"))}
                  </small>
                </FormGroup>

                <Row className="mb-4">
                  <Col md="6">
                    <FormGroup>
                      <Label>{titleCase(t("start date"))}</Label>
                      <Input
                        type="date"
                        value={event.startDate || ""}
                        onChange={(e) => updateArrayField("events", eventIndex, "startDate", e.target.value)}
                        disabled={!canManage}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>{titleCase(t("end date"))}</Label>
                      <Input
                        type="date"
                        value={event.endDate || ""}
                        min={event.startDate || undefined}
                        onChange={(e) => updateArrayField("events", eventIndex, "endDate", e.target.value)}
                        disabled={!canManage}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Tag */}
                <FormGroup className="mb-4">
                  <Label>{titleCase(t("tag"))}</Label>
                  <Row>
                    {LANGS.map((lang) => (
                      <Col md="4" key={lang}>
                        <Label className="small text-muted">{lang.toUpperCase()}</Label>
                        <Input
                          value={event.tag?.[lang] || ""}
                          placeholder={titleCase(t("event tag"))}
                          onChange={(e) =>
                            updateArrayField("events", eventIndex, "tag", {
                              ...(event.tag || emptyLang()),
                              [lang]: e.target.value,
                            })
                          }
                          disabled={!canManage}
                        />
                      </Col>
                    ))}
                  </Row>
                </FormGroup>
                {/* Additional features */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-1">{titleCase(t("additional features"))}</h6>
                    <small className="text-muted">
                      {titleCase(t("select an icon and provide a short description"))}
                    </small>
                  </div>
                  {canManage && (
                    <Button
                      type="button"
                      size="sm"
                      color="primary"
                      outline
                      className="d-flex align-items-center gap-1"
                      onClick={() => addFeature(eventIndex)}
                    >
                      <Plus size={15} />
                      {titleCase(t("add feature"))}
                    </Button>
                  )}
                </div>

                {(event.additionalFeatures || [])
                  .map(
                    (feature, featureIndex) => (
                      <Card
                        className="mb-3 bg-light"
                        key={featureIndex}
                      >
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3 text-dark">
                            <strong>
                              {titleCase(t("feature"))}{" "}{featureIndex + 1}
                            </strong>

                            {canManage && (
                              <Button
                                type="button"
                                size="sm"
                                color="danger"
                                outline
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: "30px", height: "30px", padding: 0 }}
                                onClick={() => removeFeature(eventIndex, featureIndex)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>

                          {/* Icon */}
                          <FormGroup>
                            <Label className="text-dark">
                              {titleCase(t("icon"))}
                            </Label>

                            <small className="text-muted d-block mb-3">
                              {titleCase(t("click an icon below to select it"))}
                            </small>

                            <div
                              className="d-flex flex-wrap gap-2"
                              style={{
                                rowGap: "12px",
                              }}
                            >
                              {Object.entries(ICON_MAP).map(([name, Icon]) => {
                                const active = feature.icon === name;

                                return (
                                  <button
                                    key={name}
                                    type="button"
                                    className={`border rounded d-flex align-items-center justify-content-center ${active ? "bg-primary" : "bg-white"}`}
                                    style={{ width: 44, height: 44, padding: 0, cursor: "pointer", transition: "all 0.2s ease" }}
                                    onClick={() => updateFeatureField(eventIndex, featureIndex, "icon", name)}
                                    title={name.replace("Fa", "")}
                                    disabled={!canManage}
                                  >
                                    <Icon size={18} className={active ? "text-white" : ""} />
                                  </button>
                                );
                              })}
                            </div>
                          </FormGroup>

                          {/* Description */}
                          <Row>
                            {LANGS.map((lang) => (
                              <Col md="4" key={lang}>
                                <FormGroup>
                                  <Label className="text-dark">
                                    {titleCase(t("description"))} - {lang.toUpperCase()}
                                  </Label>
                                  <Input
                                    type="textarea"
                                    rows="3"
                                    value={feature.description?.[lang] || ""}
                                    placeholder={titleCase(t("short description"))}
                                    onChange={(e) =>
                                      updateFeatureLangField(eventIndex, featureIndex, lang, e.target.value)
                                    }
                                    disabled={!canManage}
                                  />
                                </FormGroup>
                              </Col>
                            ))}
                          </Row>
                        </CardBody>
                      </Card>
                    )
                  )}

                {(event.additionalFeatures ||
                  []).length === 0 && (
                  <div className="text-muted text-center py-3 border rounded">
                    {titleCase(t("no additional features configured"))}
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>
    </>
  );
};

export default Events;